import { useMemo } from 'react';
import { type Edge, type ReactFlowState, useStore } from 'reactflow';
import { layoutNodes, nodeNeedsMeasuring } from './layout';
import type { FlowNode, StepSlotHeight } from './types';
import { STEP_SLOT_HEIGHT } from '../Steps/constants';

type MeasuredSizes = Record<string, { width: number; height: number }>;

const measuredSizesSelector = ({ nodeInternals }: ReactFlowState) => {
  const sizes: MeasuredSizes = {};
  nodeInternals.forEach((node) => {
    if (node.width && node.height && nodeNeedsMeasuring(node as FlowNode)) {
      sizes[node.id] = { width: node.width, height: node.height };
    }
  });
  return sizes;
};

const measuredSizesEqual = (a: MeasuredSizes, b: MeasuredSizes) => {
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every(
      (id) =>
        b[id] && a[id].width === b[id].width && a[id].height === b[id].height
    )
  );
};

type LayoutOptions = {
  stepSlotHeight?: StepSlotHeight;
  selectedId?: string | null;
};

type FlowLayout = {
  nodes: FlowNode[];
  ready: boolean;
};

// Lays out the flow within the same render the nodes are rendered in.
// Almost every node has a static size (step cards sit in a fixed slot),
// so the layout comes straight from the data. The few content-sized nodes (triggers,
// variables) are measured by the visible flow itself: react-flow keeps them hidden until
// its ResizeObserver reports their size, which re-runs this layout synchronously before
// the browser paints, so no intermediate positions are ever shown.
// `ready` tells whether every node that needs measuring has been measured,
// i.e. whether the returned positions are final.
export const useFlowLayout = (
  nodes: FlowNode[],
  edges: Edge[],
  { stepSlotHeight = STEP_SLOT_HEIGHT.edit, selectedId }: LayoutOptions
): FlowLayout => {
  const measuredSizes = useStore(measuredSizesSelector, measuredSizesEqual);

  const layout = useMemo(() => {
    let ready = true;
    const sizedNodes = nodes.map((node) => {
      if (!nodeNeedsMeasuring(node)) {
        return node;
      }
      const size = measuredSizes[node.id];
      if (!size) {
        ready = false;
        return node;
      }
      return { ...node, ...size };
    });

    return { nodes: layoutNodes(sizedNodes, edges, stepSlotHeight), ready };
  }, [nodes, edges, measuredSizes, stepSlotHeight]);

  return useMemo(() => {
    return {
      ...layout,
      nodes: selectedId
        ? layout.nodes.map((node) =>
            // Selecting the node being placed elevates it above the rest while it's dragged
            node.id === selectedId ? { ...node, selected: true } : node
          )
        : layout.nodes,
    };
  }, [layout, selectedId]);
};
